import { animate, state, style, transition, trigger, keyframes, query, stagger } from '@angular/animations';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise } from 'rxjs/operators';
import { isNumeric } from 'rxjs/internal-compatibility';
import { number } from 'card-validator';
import { Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import * as momentNs from 'moment-timezone';
import { DOCUMENT, CommonModule, isPlatformBrowser } from '@angular/common';
import { EventEmitter, Injectable, APP_INITIALIZER, NgModule, Directive, ElementRef, HostListener, Output, Inject, Input, Pipe, PLATFORM_ID } from '@angular/core';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** *
 * Animations settings are stored here
  @type {?} */
var ANIMATIONS = {
    speed: {
        default: '225ms',
        slow: '450ms',
        leave: '195ms'
    },
    bezierCurve: {
        default: 'ease-in-out'
    }
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** *
 * Animates show/hide elements
 *
 * \@example
 * [\@fadeAnimation]="true"
 *
  @type {?} */
var fadeAnimation = trigger('fadeAnimation', [
    state('true', style({
        opacity: 0,
        display: 'none'
    })),
    state('false', style({
        opacity: 1,
        display: 'block'
    })),
    transition('* => *', animate(ANIMATIONS.speed.default + " " + ANIMATIONS.bezierCurve.default))
]);

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** *
 * Animates show/hide router
 *
 * \@example
 * [\@fadeInAnimation]="true"
 *
  @type {?} */
var fadeInAnimation = trigger('fadeInAnimation', [
    transition(':enter', [
        // styles at start of transition
        style({ opacity: 0 }),
        // animation and styles at end of transition
        animate(ANIMATIONS.speed.default, style({ opacity: 1 }))
    ])
]);

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** *
 * Animates the :enter event of ngFor
 *
 * \@example
 * [\@ngForAnimation]="data.length"
 *
  @type {?} */
var enterSlideUpAnimation = trigger('ngForAnimation', [
    transition('* => *', [
        query(':enter', style({ opacity: 0 }), { optional: true }),
        query(':enter', stagger(ANIMATIONS.speed.default, [
            animate(ANIMATIONS.speed.slow + " " + ANIMATIONS.bezierCurve.default, keyframes([
                style({ opacity: 0, transform: 'translateY(10%)' }),
                style({ opacity: .5, transform: 'translateY(35px)' }),
                style({ opacity: 1, transform: 'translateY(0)' })
            ]))
        ]), { optional: true })
    ])
]);

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** *
 * \@example
 * [\@ngForAnimationFold]="item.open ? 'opened' : 'closed'"
 *
  @type {?} */
var foldDownUpAnimation = trigger('ngForAnimationFold', [
    state('closed', style({
        height: 0,
        opacity: 0,
        pointerEvents: 'none'
    })),
    state('opened', style({
        height: '*',
        opacity: 1,
        pointerEvents: 'inherit'
    })),
    transition('closed => opened', [
        animate(ANIMATIONS.speed.default + " " + ANIMATIONS.bezierCurve.default, keyframes([
            style({ height: 0, opacity: 0 }),
            style({ height: '*', opacity: 0.3 }),
            style({ height: '*', opacity: 1 })
        ]))
    ]),
    transition('opened => closed', [
        animate(ANIMATIONS.speed.leave + " " + ANIMATIONS.bezierCurve.default, keyframes([
            style({ height: '*', opacity: 1 }),
            style({ height: '*', opacity: 0.3 }),
            style({ height: 0, opacity: 0 })
        ]))
    ])
]);

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** *
 * Animates :enter and :leave
 *
 * \@example
 * [\@fadeInFadeOutAnimation]
 *
  @type {?} */
var fadeInFadeOutAnimation = trigger('fadeInFadeOutAnimation', [
    transition(':enter', [
        // styles at start of transition
        style({ opacity: 0 }),
        // animation and styles at end of transition
        animate(ANIMATIONS.speed.default, style({ opacity: 1 }))
    ])
]);

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @return {?}
 */
function uniqueId() {
    /** @type {?} */
    var text = '';
    /** @type {?} */
    var possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (var i = 0; i < 5; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
}
var ConfigureService = /** @class */ (function () {
    function ConfigureService(http) {
        this.http = http;
        this.configFile = 'config.json';
        this.paramsLoaded = new EventEmitter();
    }
    /**
     * @return {?}
     */
    ConfigureService.prototype.load = /**
     * @return {?}
     */
    function () {
        var _this = this;
        return this.http.get(this.configFile + "?t=" + uniqueId())
            .toPromise()
            .then(function (params) {
            _this.params = params;
            _this.paramsLoaded.emit(params);
        }, function () {
            throw new Error(_this.configFile + " could not be loaded / found");
        });
    };
    ConfigureService.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    ConfigureService.ctorParameters = function () { return [
        { type: HttpClient }
    ]; };
    return ConfigureService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var ConfigureModule = /** @class */ (function () {
    function ConfigureModule() {
    }
    /**
     * @return {?}
     */
    ConfigureModule.forRoot = /**
     * @return {?}
     */
    function () {
        return {
            ngModule: ConfigureModule,
            providers: [ConfigureService]
        };
    };
    ConfigureModule.decorators = [
        { type: NgModule, args: [{
                    imports: [
                        HttpClientModule
                    ],
                    declarations: [],
                    providers: [
                        ConfigureService,
                        {
                            provide: APP_INITIALIZER,
                            useFactory: init,
                            multi: true,
                            deps: [ConfigureService]
                        }
                    ]
                },] }
    ];
    return ConfigureModule;
}());
/**
 * @param {?} configService
 * @return {?}
 */
function init(configService) {
    return function () { return configService.load(); };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Emit a event when clicked outside of the element
 *
 * __Usage :__
 *   (ngxClickOutside)="closeDropdown()"
 *
 * __Example :__
 *   <div (ngxClickOutside)="closeDropdown()"></div>
 *
 * Module {\@link ClickOutsideSharedDirectiveModule}
 */
var ClickOutsideDirective = /** @class */ (function () {
    function ClickOutsideDirective(elementRef) {
        this.elementRef = elementRef;
        // tslint:disable-next-line:prefer-output-readonly
        this.ngxClickOutside = new EventEmitter();
    }
    /**
     * @param {?} event
     * @return {?}
     */
    ClickOutsideDirective.prototype.onDocumentClick = /**
     * @param {?} event
     * @return {?}
     */
    function (event) {
        /** @type {?} */
        var targetElement = /** @type {?} */ (event.target);
        // Check if the click was outside the element
        if (targetElement && !this.elementRef.nativeElement.contains(targetElement)) {
            this.ngxClickOutside.emit(event);
        }
    };
    ClickOutsideDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[ngxClickOutside]'
                },] }
    ];
    /** @nocollapse */
    ClickOutsideDirective.ctorParameters = function () { return [
        { type: ElementRef }
    ]; };
    ClickOutsideDirective.propDecorators = {
        ngxClickOutside: [{ type: Output }],
        onDocumentClick: [{ type: HostListener, args: ['document:click', ['$event'],] }]
    };
    return ClickOutsideDirective;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * The ClickOutsideSharedDirectiveModule module
 *
 * import { ClickOutsideSharedDirectiveModule } from 'styleguide/src/shared/directive/click.outside/click.outside.module';
 */
var ClickOutsideSharedDirectiveModule = /** @class */ (function () {
    function ClickOutsideSharedDirectiveModule() {
    }
    ClickOutsideSharedDirectiveModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        ClickOutsideDirective
                    ],
                    imports: [],
                    exports: [
                        ClickOutsideDirective
                    ]
                },] }
    ];
    return ClickOutsideSharedDirectiveModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @return {?}
 */
function _window() {
    // return the global native browser window object
    return window;
}
/**
 * This service is a window service
 *
 * \@example
 * WindowRefService.nativeWindow;
 *
 */
var WindowRefService = /** @class */ (function () {
    function WindowRefService() {
    }
    Object.defineProperty(WindowRefService.prototype, "nativeWindow", {
        get: /**
         * @return {?}
         */
        function () {
            return _window();
        },
        enumerable: true,
        configurable: true
    });
    WindowRefService.decorators = [
        { type: Injectable }
    ];
    return WindowRefService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var InfiniteScrollService = /** @class */ (function () {
    function InfiniteScrollService(windowRef, document) {
        this.windowRef = windowRef;
        this.document = document;
        this.loadMore = new Subject();
        this.loadMore$ = this.loadMore;
    }
    /**
     * @param {?} offset
     * @param {?} offsetElements
     * @return {?}
     */
    InfiniteScrollService.prototype.createScrollWatcher = /**
     * @param {?} offset
     * @param {?} offsetElements
     * @return {?}
     */
    function (offset, offsetElements) {
        var _this = this;
        /** @type {?} */
        var scroll$ = fromEvent(this.windowRef.nativeWindow, 'scroll', { passive: true }).pipe(map(function () {
            /** @type {?} */
            var removeHeight = offset;
            offsetElements.map(function (el) {
                /** @type {?} */
                var element = _this.document.querySelector(el);
                if (element)
                    removeHeight = removeHeight + element.offsetHeight;
            });
            /** @type {?} */
            var toBottom = _this.getDistFromBottom(removeHeight);
            return toBottom;
        }), pairwise(), filter(function (data) { return data[1] < data[0]; }), filter(function (data) { return data[1] === 0; }), distinctUntilChanged(), debounceTime(100));
        this.scrollSub$ = scroll$.subscribe(function (data) {
            _this.loadMore.next(true);
        });
    };
    /**
     * @return {?}
     */
    InfiniteScrollService.prototype.destroyScrollWatcher = /**
     * @return {?}
     */
    function () {
        this.scrollSub$.unsubscribe();
    };
    /**
     * @param {?} deduct
     * @return {?}
     */
    InfiniteScrollService.prototype.getDistFromBottom = /**
     * @param {?} deduct
     * @return {?}
     */
    function (deduct) {
        /** @type {?} */
        var scrollPosition = this.windowRef.nativeWindow.pageYOffset;
        /** @type {?} */
        var windowSize = this.windowRef.nativeWindow.innerHeight;
        /** @type {?} */
        var bodyHeight = this.document.body.offsetHeight;
        return Math.max((bodyHeight - deduct) - (scrollPosition + windowSize), 0);
    };
    InfiniteScrollService.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    InfiniteScrollService.ctorParameters = function () { return [
        { type: WindowRefService },
        { type: undefined, decorators: [{ type: Inject, args: [DOCUMENT,] }] }
    ]; };
    return InfiniteScrollService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var InfiniteScrollDirective = /** @class */ (function () {
    function InfiniteScrollDirective(infinScrService) {
        this.infinScrService = infinScrService;
        this.infiniteScrOffset = 0;
    }
    /**
     * @return {?}
     */
    InfiniteScrollDirective.prototype.ngAfterViewInit = /**
     * @return {?}
     */
    function () {
        this.infinScrService.createScrollWatcher(this.infiniteScrOffset, this.infiniteScrOffsetElements);
    };
    InfiniteScrollDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[ngxInfiniteScroll]'
                },] }
    ];
    /** @nocollapse */
    InfiniteScrollDirective.ctorParameters = function () { return [
        { type: InfiniteScrollService }
    ]; };
    InfiniteScrollDirective.propDecorators = {
        infiniteScrOffsetElements: [{ type: Input }],
        infiniteScrOffset: [{ type: Input }]
    };
    return InfiniteScrollDirective;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var InfiniteScrollSharedDirectiveModule = /** @class */ (function () {
    function InfiniteScrollSharedDirectiveModule() {
    }
    InfiniteScrollSharedDirectiveModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        InfiniteScrollDirective
                    ],
                    imports: [],
                    exports: [
                        InfiniteScrollDirective
                    ],
                    providers: [
                        WindowRefService,
                        InfiniteScrollService
                    ]
                },] }
    ];
    return InfiniteScrollSharedDirectiveModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Add parameters to srcset to support serving images through imgix.com domain.
 * Some default values are based on imgix.com documentation
 *
 * __Usage :__
 *   <img src="https://domain.com/any.png" imgService="w=100">
 *
 * __Example :__
 *   <img src="https://domain.com/any.png" srcset="https://domain.com/any.png?w=100&dpr=2 2x ..."></div>
 *
 */
var ImgServiceDirective = /** @class */ (function () {
    function ImgServiceDirective(elementRef) {
        this.elementRef = elementRef;
        this.defaultParams = 'auto=format,compress';
    }
    /**
     * @return {?}
     */
    ImgServiceDirective.prototype.ngAfterViewInit = /**
     * @return {?}
     */
    function () {
        this.applyChanges(this.elementRef.nativeElement.src);
    };
    /**
     * @param {?} src
     * @return {?}
     */
    ImgServiceDirective.prototype.applyChanges = /**
     * @param {?} src
     * @return {?}
     */
    function (src) {
        if (src && src.substr(-1) !== '/') {
            /** @type {?} */
            var params = this.params ? "?" + this.params + "&" : '?';
            /** @type {?} */
            var srcParams = "" + src + params + this.defaultParams;
            this.elementRef.nativeElement.src = "" + srcParams;
            this.elementRef.nativeElement.srcset = srcParams + "&dpr=1 1x, " + srcParams + "&dpr=2 2x, " + srcParams + "&dpr=3 3x";
        }
    };
    /**
     * @param {?} changes
     * @return {?}
     */
    ImgServiceDirective.prototype.ngOnChanges = /**
     * @param {?} changes
     * @return {?}
     */
    function (changes) {
        this.applyChanges(this.imgServiceBinding);
    };
    ImgServiceDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[imgService]'
                },] }
    ];
    /** @nocollapse */
    ImgServiceDirective.ctorParameters = function () { return [
        { type: ElementRef }
    ]; };
    ImgServiceDirective.propDecorators = {
        params: [{ type: Input, args: ['imgService',] }],
        imgServiceBinding: [{ type: Input }]
    };
    return ImgServiceDirective;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * The ImgServiceDirectiveModule module
 */
var ImgServiceDirectiveModule = /** @class */ (function () {
    function ImgServiceDirectiveModule() {
    }
    ImgServiceDirectiveModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        ImgServiceDirective
                    ],
                    imports: [],
                    exports: [
                        ImgServiceDirective
                    ]
                },] }
    ];
    return ImgServiceDirectiveModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} arr
 * @param {?} depth
 * @return {?}
 */
function sortAlphanumericArray(arr, depth) {
    return arr.sort(function (a, b) {
        /** @type {?} */
        var valueA = a;
        /** @type {?} */
        var valueB = b;
        depth.forEach(function (val) {
            valueA = valueA[val];
            valueB = valueB[val];
            if (typeof (valueA) === 'undefined') {
                throw new Error("cannot find object with depth \"" + val + "\"");
            }
        });
        /** @type {?} */
        var nameA = isNumeric(valueA) ? valueA.toLocaleString() : valueA.toLowerCase();
        /** @type {?} */
        var nameB = isNumeric(valueB) ? valueB.toLocaleString() : valueB.toLowerCase();
        if (nameA < nameB) {
            return -1; // sort string ascending
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    });
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/*
 * https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
 * This is MLP email address Regular Expression.
 *
 * Usage:
 *    import { EMAIL } from './validators.expressions';
 *
 * Example:
 *    const TEST = new RegExp(EMAIL)
*/
/** @type {?} */
var EMAIL = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/** @type {?} */
var DOUBLEAT = /^@$/g;
/** @type {?} */
var PHONENUMBER = /^[(]?(\+[\d]{1,3}\s?)?[\d ()]{7,}$/g;

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @return {?}
 */
function mlpEmailValidator() {
    return function (control) {
        /** @type {?} */
        var hasRegExp;
        /** @type {?} */
        var EMAILMATCH = (control.value) ? control.value.match(EMAIL) : undefined;
        /** @type {?} */
        var DOUBLEATMATCH = (control.value) ? control.value.match(DOUBLEAT) : undefined;
        if (EMAILMATCH)
            hasRegExp = true;
        if (EMAILMATCH && DOUBLEATMATCH && DOUBLEATMATCH.length) {
            hasRegExp = (DOUBLEATMATCH.length >= 2) ? true : false;
        }
        return hasRegExp ? undefined : { invalidEmail: { value: control.value } };
    };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @return {?}
 */
function mlpPhoneNumberValidator() {
    return function (control) {
        /** @type {?} */
        var isValid = false;
        /** @type {?} */
        var data = control.value;
        if (data && data.match(PHONENUMBER)) {
            isValid = true;
        }
        return isValid ? undefined : { invalidPhoneNumber: { value: data } };
    };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} value
 * @return {?}
 */
function isEmptyInputValue(value) {
    // we don't check for string here so it also works with arrays
    return value == null || value.length === 0;
}
/**
 * @return {?}
 */
function customNotEmptySpaceValidator() {
    return function (control) {
        if (isEmptyInputValue(control.value)) {
            return null; // don't validate empty values to allow optional controls
        }
        if (control.value.replace(/ +(?= )/g, '') === ' ') {
            return { notEmptySpace: true };
        }
        return null;
    };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @return {?}
 */
function customSelectRequiredValidator() {
    return function (control) {
        /** @type {?} */
        var isValid = false;
        /** @type {?} */
        var data = /** @type {?} */ (control.value);
        if (data && data.data) {
            isValid = true;
        }
        return isValid ? undefined : { invalidSelectRequired: { value: data } };
    };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} control
 * @return {?}
 */
function pfCreditCardValidator(control) {
    /** @type {?} */
    var valid = number(control.value);
    return valid.isValid || valid.isPotentiallyValid ? null : { cardNumber: { valid: false } };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var V_EMAIL = {
    requirements: [Validators.required, mlpEmailValidator()],
    errorMessage: ['the field must contain a valid email address']
};
/** @type {?} */
var V_PHONE_NUMBER = {
    requirements: [Validators.required, mlpPhoneNumberValidator()],
    errorMessage: ['A phone number can only contain 0-9 + ( ) and minimum 7 characters']
};
/** @type {?} */
var V_FULL_NAME = {
    requirements: [Validators.required],
    errorMessage: ['This field is required']
};
/** @type {?} */
var V_PASSWORD = {
    requirements: [Validators.required],
    errorMessage: ['This field is required']
};
/** @type {?} */
var V_PASSWORD_REPEAT = {
    requirements: [Validators.required],
    errorMessage: ['This field needs to be the same as your password field']
};
/** @type {?} */
var V_GUEST_CHECKOUT = {
    requirements: [],
    errorMessage: ['We need your details for your account and any bookings you make.']
};
/** @type {?} */
var V_GUEST_CHECKOUT_FULL_NAME = {
    requirements: [],
    errorMessage: ['This field is required']
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var PasswordStrength = /** @class */ (function () {
    function PasswordStrength() {
    }
    /**
     * @param {?} password
     * @return {?}
     */
    PasswordStrength.prototype.calculate = /**
     * @param {?} password
     * @return {?}
     */
    function (password) {
        /** @type {?} */
        var strength = 0;
        if (!password) {
            return 0;
        }
        if (password.length > 8) { // check if more than 8 characters
            // check if more than 8 characters
            strength += 25;
        }
        if (password.match(/(?=.*[0-9])/g)) { // check if contains numbers
            // check if contains numbers
            strength += 25;
        }
        if (password.match(/(?=.*[a-z])(?=.*[A-Z])/g)) { // check if contains lower and uppercase
            // check if contains lower and uppercase
            strength += 25;
        }
        if (password.match(/(?=.*[-+_!@#$%^&*.,?])/g)) { // check if contains special characters
            // check if contains special characters
            strength += 25;
        }
        return strength;
    };
    return PasswordStrength;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var Pitch = /** @class */ (function () {
    function Pitch() {
        this.type = 'pitches';
    }
    return Pitch;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var Venue = /** @class */ (function () {
    function Venue() {
        this.type = 'venues';
    }
    return Venue;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Converts and interger(minutes) to text time
 *
 * __Usage :__
 *   value | durationToText
 *
 * __Example :__
 *   {{ 90 |  durationToText }}
 *   formats to: 1 hour 30 minutes
 *   {{ 90 |  durationToText:true }}
 *   formats to: 1h 30m
 *
 * Module {\@link TransformDurationToTextModule}
 */
var TransformDurationToTextPipe = /** @class */ (function () {
    function TransformDurationToTextPipe() {
    }
    /**
     * @param {?} value
     * @param {?=} short
     * @return {?}
     */
    TransformDurationToTextPipe.prototype.transform = /**
     * @param {?} value
     * @param {?=} short
     * @return {?}
     */
    function (value, short) {
        if (short === void 0) { short = false; }
        /** @type {?} */
        var returnText = '';
        if (value) {
            /** @type {?} */
            var hours = Math.floor(value / 60);
            /** @type {?} */
            var minutes = value % 60;
            if (hours > 0) {
                /** @type {?} */
                var hoursSyntax = short ? 'h ' : (hours === 1 ? ' hour ' : ' hours ');
                returnText = hours + hoursSyntax;
            }
            if (minutes > 0) {
                /** @type {?} */
                var minutesSyntax = short ? 'm' : (minutes === 1 ? ' minute' : ' minutes');
                returnText = returnText + minutes + minutesSyntax;
            }
        }
        return returnText.trim();
    };
    TransformDurationToTextPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'durationToText'
                },] }
    ];
    return TransformDurationToTextPipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * The TransformDurationToText module
 *
 * import { TransformDurationToTextModule } from 'styleguide/src/shared/pipe/transform-duration-to-text.module';
 */
var TransformDurationToTextModule = /** @class */ (function () {
    function TransformDurationToTextModule() {
    }
    TransformDurationToTextModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        TransformDurationToTextPipe
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        TransformDurationToTextPipe
                    ]
                },] }
    ];
    return TransformDurationToTextModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var moment = momentNs;
/** @type {?} */
var ZONE = 'Europe/London';
/**
 * Converts time string to how long ago
 *
 * __Usage :__
 *   time | ngxAgo: endTime
 *
 * __Example :__
 *   {{ '2017-01-01T15:00:00+00:00' | ngxAgo: '2017-01-02T15:00:00+00:00' }}
 *   formats to: 1 day
 *   {{ '2018-01-01T15:00:00+00:00' | ngxAgo }}
 *   formats to: 30 minutes
 *
 * Module {\@link TransformAgoModule}
 */
var TransformAgoPipe = /** @class */ (function () {
    function TransformAgoPipe() {
    }
    /**
     * @param {?} date
     * @param {?=} end
     * @return {?}
     */
    TransformAgoPipe.prototype.transform = /**
     * @param {?} date
     * @param {?=} end
     * @return {?}
     */
    function (date, end) {
        /** @type {?} */
        var endTime = (end) ? moment.tz(end, ZONE) : moment.tz(ZONE);
        /** @type {?} */
        var start = moment.tz(date, ZONE);
        /** @type {?} */
        var diffs = moment.duration(endTime.diff(start));
        return diffs.humanize();
    };
    TransformAgoPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'ngxAgo'
                },] }
    ];
    return TransformAgoPipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * The TransformAgoModule module
 *
 * import { TransformAgoModule } from 'styleguide/src/shared/pipe/ago/pipe.ago.module';
 */
var TransformAgoModule = /** @class */ (function () {
    function TransformAgoModule() {
    }
    TransformAgoModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        TransformAgoPipe
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        TransformAgoPipe
                    ]
                },] }
    ];
    return TransformAgoModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var TransformCamelCaseEsportsPipe = /** @class */ (function () {
    function TransformCamelCaseEsportsPipe() {
    }
    /**
     * @param {?} str
     * @return {?}
     */
    TransformCamelCaseEsportsPipe.prototype.transform = /**
     * @param {?} str
     * @return {?}
     */
    function (str) {
        if (str === null)
            return '';
        if (str.toLowerCase() === 'esports')
            return 'eSports';
        if (str.toLowerCase() === 'esport')
            return 'eSport';
        return str.replace(/\w\S*/g, function (txt) {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        });
    };
    TransformCamelCaseEsportsPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'ngxCamelCaseEsports'
                },] }
    ];
    return TransformCamelCaseEsportsPipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var TransformCamelCaseEsportsModule = /** @class */ (function () {
    function TransformCamelCaseEsportsModule() {
    }
    TransformCamelCaseEsportsModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        TransformCamelCaseEsportsPipe
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        TransformCamelCaseEsportsPipe
                    ]
                },] }
    ];
    return TransformCamelCaseEsportsModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var TransformCapitalizeFirstPipe = /** @class */ (function () {
    function TransformCapitalizeFirstPipe() {
    }
    /**
     * @param {?} value
     * @return {?}
     */
    TransformCapitalizeFirstPipe.prototype.transform = /**
     * @param {?} value
     * @return {?}
     */
    function (value) {
        if (value === null)
            return 'Not assigned';
        return value.charAt(0).toUpperCase() + value.slice(1);
    };
    TransformCapitalizeFirstPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'ngxCapitalizeFirst'
                },] }
    ];
    return TransformCapitalizeFirstPipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * The TransformAgoModule module
 *
 * import { TransformAgoModule } from 'styleguide/src/shared/pipe/capitalizeFirstLetter/pipe.capitalize.first.letter.module';
 */
var TransformCapitalizeFirstModule = /** @class */ (function () {
    function TransformCapitalizeFirstModule() {
    }
    TransformCapitalizeFirstModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        TransformCapitalizeFirstPipe
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        TransformCapitalizeFirstPipe
                    ]
                },] }
    ];
    return TransformCapitalizeFirstModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Appends st, nd, th to number
 *
 * __Usage :__
 *   day | ngxDateNth
 *
 * __Example :__
 *   {{ 27 | ngxDateNth }}
 *   formats to: 27th
 *
 * Module {\@link TransformDateNthModule}
 */
var TransformDateNthPipe = /** @class */ (function () {
    function TransformDateNthPipe() {
    }
    /**
     * @param {?} day
     * @return {?}
     */
    TransformDateNthPipe.prototype.transform = /**
     * @param {?} day
     * @return {?}
     */
    function (day) {
        if (day > 3 && day < 21)
            return day + "th";
        switch (day % 10) {
            case 1: return day + "st";
            case 2: return day + "nd";
            case 3: return day + "rd";
            default: return day + "th";
        }
    };
    TransformDateNthPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'ngxDateNth'
                },] }
    ];
    return TransformDateNthPipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * The TransformDateNth module
 *
 * import { TransformDateNthModule } from 'styleguide/src/shared/pipe/dateNth/pipe.dateNth.module';
 */
var TransformDateNthModule = /** @class */ (function () {
    function TransformDateNthModule() {
    }
    TransformDateNthModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        TransformDateNthPipe
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        TransformDateNthPipe
                    ]
                },] }
    ];
    return TransformDateNthModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Get the duraton between start date and end date in minutes
 *
 * __Usage :__
 *   startDate | ngxDuration: endDate
 *
 * __Example :__
 *   {{ 2017-08-27T16:30:00.000Z | ngxDuration: 2017-08-27T17:30:00.000Z }}
 *   formats to: 60
 *
 * Module {\@link TransformDurationToTextModule}
 */
var TransformDurationPipe = /** @class */ (function () {
    function TransformDurationPipe() {
    }
    /**
     * @param {?} startDate
     * @param {?} endDate
     * @return {?}
     */
    TransformDurationPipe.prototype.transform = /**
     * @param {?} startDate
     * @param {?} endDate
     * @return {?}
     */
    function (startDate, endDate) {
        /** @type {?} */
        var diffs = 0;
        if (startDate && endDate) {
            /** @type {?} */
            var endTime = DateTime.fromISO(endDate);
            /** @type {?} */
            var start = DateTime.fromISO(startDate);
            diffs = endTime.diff(start, ['minutes']).toObject().minutes;
            if (diffs < 0 || typeof diffs === 'undefined')
                diffs = 0;
        }
        return diffs;
    };
    TransformDurationPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'ngxDuration'
                },] }
    ];
    return TransformDurationPipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * The TransformDuration module
 *
 * import { TransformDurationModule } from 'styleguide/src/shared/pipe/duration/pipe.duration.module';
 */
var TransformDurationModule = /** @class */ (function () {
    function TransformDurationModule() {
    }
    TransformDurationModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        TransformDurationPipe
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        TransformDurationPipe
                    ]
                },] }
    ];
    return TransformDurationModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var SPORT_ICONS = {
    athletics: 'custom-athletics',
    badminton: 'badminton-shuttlecock',
    basketball: 'basketball-ball',
    cricket: 'cricket-bat-ball',
    esports: 'video-game-xbox-controller',
    football: 'soccer-ball',
    futsal: 'custom-futstal',
    gaa: 'custom-gaelic-football',
    golf: 'golf-ball',
    gym: 'fitness-dumbbell',
    handball: 'custom-handball',
    hockey: 'custom-hockey',
    lacrosse: 'custom-lacrosse',
    netball: 'custom-netball',
    quidditch: 'custom-quidditch',
    rugby: 'custom-rugby',
    'space-hire': 'custom-space-hire',
    squash: 'custom-squash',
    swimming: 'swimming-pool-stairs',
    'table-tennis': 'ping-pong-paddle',
    tennis: 'custom-tennis',
    volleyball: 'volleyball-ball',
    'american-football': 'american-football-helmet',
    'padel-tennis': 'custom-padel',
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var TransformSportToIconNamePipe = /** @class */ (function () {
    function TransformSportToIconNamePipe() {
    }
    /**
     * @param {?} sport
     * @return {?}
     */
    TransformSportToIconNamePipe.prototype.transform = /**
     * @param {?} sport
     * @return {?}
     */
    function (sport) {
        for (var prop in SPORT_ICONS) {
            if (SPORT_ICONS.hasOwnProperty(prop) && prop === sport) {
                return SPORT_ICONS[prop];
            }
        }
        return sport;
    };
    TransformSportToIconNamePipe.decorators = [
        { type: Pipe, args: [{
                    name: 'sportIconPipe'
                },] }
    ];
    return TransformSportToIconNamePipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var TransformSportIconModule = /** @class */ (function () {
    function TransformSportIconModule() {
    }
    TransformSportIconModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        TransformSportToIconNamePipe
                    ],
                    exports: [
                        TransformSportToIconNamePipe
                    ]
                },] }
    ];
    return TransformSportIconModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var moment$1 = momentNs;
/**
 * Converts a date string to format
 * https://momentjs.com/timezone/
 *
 * __Usage :__
 *   day | ngxTime
 *
 * __Example :__
 *   {{ '2019-01-29T09:00:00+01:00' | ngxTime }}
 *   formats to: 09:00
 *
 * Module {\@link TransformTimeModule}
 */
var TransformTimePipe = /** @class */ (function () {
    function TransformTimePipe() {
    }
    /**
     * @param {?} date
     * @param {?=} format
     * @return {?}
     */
    TransformTimePipe.prototype.transform = /**
     * @param {?} date
     * @param {?=} format
     * @return {?}
     */
    function (date, format) {
        if (format === void 0) { format = 'HH:mm'; }
        /** @type {?} */
        var moDate = moment$1.tz(date, 'Europe/London');
        if (moDate._isValid) {
            return moDate.format(format);
        }
        return date;
    };
    TransformTimePipe.decorators = [
        { type: Pipe, args: [{
                    name: 'ngxTime'
                },] }
    ];
    return TransformTimePipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * The TransformDuration module
 *
 * import { TransformTimeModule } from 'styleguide/src/shared/pipe/time/pipe.time.module';
 */
var TransformTimeModule = /** @class */ (function () {
    function TransformTimeModule() {
    }
    TransformTimeModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        TransformTimePipe
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        TransformTimePipe
                    ]
                },] }
    ];
    return TransformTimeModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Transforms api data into relationships from includes
 *
 * __Example :__
 *   this.http.get('/endpoint?&include=pitch,staffMember')
 *   .map(data => this.transformer.mapOneInCollection(data, [{ pitch: 'pitches' }, { staffMember: 'staff-members' }]))
 *
 */
var  /**
 * Transforms api data into relationships from includes
 *
 * __Example :__
 *   this.http.get('/endpoint?&include=pitch,staffMember')
 *   .map(data => this.transformer.mapOneInCollection(data, [{ pitch: 'pitches' }, { staffMember: 'staff-members' }]))
 *
 */
TransformApiData = /** @class */ (function () {
    function TransformApiData() {
    }
    /**
     * @param {?} apiData
     * @param {?} relationshipToType
     * @return {?}
     */
    TransformApiData.prototype.mapOne = /**
     * @param {?} apiData
     * @param {?} relationshipToType
     * @return {?}
     */
    function (apiData, relationshipToType) {
        relationshipToType.forEach(function (relationshipKeyValue) {
            Object.keys(relationshipKeyValue).forEach(function (relKeyName) {
                if (apiData.included && apiData.included.length > 0) {
                    apiData.included.forEach(function (includedData) {
                        if (apiData.data.relationships[relKeyName].data &&
                            apiData.data.relationships[relKeyName].data.id === includedData.id &&
                            includedData.type === relationshipKeyValue[relKeyName]) {
                            apiData.data.relationships[relKeyName].data = includedData;
                        }
                    });
                }
            });
        });
        return apiData;
    };
    /**
     * @param {?} apiData
     * @param {?} relationshipToType
     * @return {?}
     */
    TransformApiData.prototype.mapArray = /**
     * @param {?} apiData
     * @param {?} relationshipToType
     * @return {?}
     */
    function (apiData, relationshipToType) {
        relationshipToType.forEach(function (relationshipKeyValue) {
            Object.keys(relationshipKeyValue).forEach(function (relKeyName) {
                if (apiData.included && apiData.included.length > 0) {
                    apiData.data.relationships[relKeyName].data.forEach(function (relationshipKey, key) {
                        apiData.included.forEach(function (includedData) {
                            if (apiData.data.relationships[relKeyName].data[key] &&
                                apiData.data.relationships[relKeyName].data[key].id === includedData.id &&
                                includedData.type === relationshipKeyValue[relKeyName]) {
                                apiData.data.relationships[relKeyName].data[key] = includedData;
                            }
                        });
                    });
                }
            });
        });
        return apiData;
    };
    /**
     * @param {?} apiData
     * @param {?} relationshipToType
     * @return {?}
     */
    TransformApiData.prototype.mapOneInCollection = /**
     * @param {?} apiData
     * @param {?} relationshipToType
     * @return {?}
     */
    function (apiData, relationshipToType) {
        relationshipToType.forEach(function (relationshipKeyValue) {
            Object.keys(relationshipKeyValue).forEach(function (relKeyName) {
                if (apiData.included && apiData.included.length > 0) {
                    apiData.data.forEach(function (entity) {
                        apiData.included.forEach(function (includedData) {
                            if (entity.relationships[relKeyName].data &&
                                entity.relationships[relKeyName].data.id === includedData.id &&
                                includedData.type === relationshipKeyValue[relKeyName]) {
                                entity.relationships[relKeyName].data = includedData;
                            }
                        });
                    });
                }
            });
        });
        return apiData;
    };
    /**
     * @param {?} apiData
     * @param {?} relationshipToType
     * @return {?}
     */
    TransformApiData.prototype.mapArrayInCollection = /**
     * @param {?} apiData
     * @param {?} relationshipToType
     * @return {?}
     */
    function (apiData, relationshipToType) {
        relationshipToType.forEach(function (relationshipKeyValue) {
            Object.keys(relationshipKeyValue).forEach(function (relKeyName) {
                if (apiData.included && apiData.included.length > 0) {
                    apiData.data.forEach(function (entity) {
                        entity.relationships[relKeyName].data.forEach(function (relationshipKey, key) {
                            apiData.included.forEach(function (includedData) {
                                if (entity.relationships[relKeyName].data[key] &&
                                    entity.relationships[relKeyName].data[key].id === includedData.id &&
                                    includedData.type === relationshipKeyValue[relKeyName]) {
                                    entity.relationships[relKeyName].data[key] = includedData;
                                }
                            });
                        });
                    });
                }
            });
        });
        return apiData;
    };
    return TransformApiData;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * This service is a array service
 */
var ArrayService = /** @class */ (function () {
    function ArrayService() {
    }
    /**
     * Splits an array in two
     *
     * @example
     * ArrayService.splitArray(Array<any>);
     *
     * param {Array<any>}
     * returns {object} { firstArray: Array<any>, secondArray: Array<any> }
     */
    /**
     * Splits an array in two
     *
     * \@example
     * ArrayService.splitArray(Array<any>);
     *
     * param {Array<any>}
     * returns {object} { firstArray: Array<any>, secondArray: Array<any> }
     * @param {?} arr
     * @return {?}
     */
    ArrayService.prototype.splitArray = /**
     * Splits an array in two
     *
     * \@example
     * ArrayService.splitArray(Array<any>);
     *
     * param {Array<any>}
     * returns {object} { firstArray: Array<any>, secondArray: Array<any> }
     * @param {?} arr
     * @return {?}
     */
    function (arr) {
        if (!Array.isArray(arr)) {
            console.error('It is not an array');
            return;
        }
        /** @type {?} */
        var mid = Math.ceil(arr.length / 2);
        /** @type {?} */
        var data = {
            firstArray: [],
            secondArray: []
        };
        data.firstArray = arr.slice(0, mid);
        data.secondArray = arr.slice(mid);
        return data;
    };
    ArrayService.decorators = [
        { type: Injectable }
    ];
    return ArrayService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * This service is a browser service
 *
 * \@example
 * BrowserService.isBrowser;
 *
 */
var BrowserService = /** @class */ (function () {
    function BrowserService(platformId) {
        this.platformId = platformId;
        /**
         * Get the isBrowser value.
         */
        this.isBrowser = isPlatformBrowser(this.platformId);
    }
    BrowserService.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    BrowserService.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] }
    ]; };
    return BrowserService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

export { ANIMATIONS, fadeAnimation, fadeInAnimation, enterSlideUpAnimation, foldDownUpAnimation, fadeInFadeOutAnimation, ConfigureService, ConfigureModule, init, ClickOutsideDirective, ClickOutsideSharedDirectiveModule, InfiniteScrollService, InfiniteScrollDirective, InfiniteScrollSharedDirectiveModule, ImgServiceDirective, ImgServiceDirectiveModule, sortAlphanumericArray, EMAIL, DOUBLEAT, PHONENUMBER, V_PHONE_NUMBER, V_EMAIL, V_FULL_NAME, V_PASSWORD, V_PASSWORD_REPEAT, V_GUEST_CHECKOUT, V_GUEST_CHECKOUT_FULL_NAME, mlpEmailValidator, mlpPhoneNumberValidator, customNotEmptySpaceValidator, customSelectRequiredValidator, pfCreditCardValidator, PasswordStrength, Pitch, Venue, TransformDurationToTextPipe, TransformDurationToTextModule, TransformAgoPipe, TransformAgoModule, TransformCamelCaseEsportsPipe, TransformCamelCaseEsportsModule, TransformCapitalizeFirstPipe, TransformCapitalizeFirstModule, TransformDateNthPipe, TransformDateNthModule, TransformDurationPipe, TransformDurationModule, TransformSportToIconNamePipe, TransformSportIconModule, TransformTimePipe, TransformTimeModule, TransformApiData, ArrayService, BrowserService, WindowRefService };

//# sourceMappingURL=pf_styleguide-shared.js.map